/**
 * Fog of War Renderer - renders fog of war overlay
 */

import * as THREE from 'three';
import type { VisionSystem } from '@/game/systems/VisionSystem';
import type { FactionId } from '@/types';

export class FogRenderer {
  private scene: THREE.Scene;
  private visionSystem: VisionSystem | null = null;
  private playerFaction: FactionId | null = null;

  private fogMesh: THREE.Mesh | null = null;
  private fogTexture: THREE.DataTexture | null = null;
  private fogData: Uint8Array | null = null;

  private updateTimer: number = 0.1; // Start ready to update immediately
  private updateInterval: number = 0.1; // Update fog every 100ms

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  setVisionSystem(vision: VisionSystem): void {
    this.visionSystem = vision;
    this.createFogMesh();
  }

  setPlayerFaction(faction: FactionId): void {
    this.playerFaction = faction;
  }

  private createFogMesh(): void {
    if (!this.visionSystem) return;

    const gridInfo = this.visionSystem.getGridInfo();
    const width = gridInfo.width;
    const height = gridInfo.height;

    // Create fog texture data (RGBA)
    this.fogData = new Uint8Array(width * height * 4);

    // Initialize with fog (same as unexplored state)
    for (let i = 0; i < width * height; i++) {
      this.fogData[i * 4] = 20;      // R
      this.fogData[i * 4 + 1] = 20;  // G
      this.fogData[i * 4 + 2] = 30;  // B
      this.fogData[i * 4 + 3] = 120; // A (medium opacity for unexplored)
    }

    // Create texture
    this.fogTexture = new THREE.DataTexture(
      this.fogData as unknown as BufferSource,
      width,
      height,
      THREE.RGBAFormat,
      THREE.UnsignedByteType
    );
    this.fogTexture.needsUpdate = true;
    this.fogTexture.minFilter = THREE.LinearFilter;
    this.fogTexture.magFilter = THREE.LinearFilter;

    // Create fog plane
    const worldWidth = width * gridInfo.size;
    const worldHeight = height * gridInfo.size;

    const geometry = new THREE.PlaneGeometry(worldWidth, worldHeight);
    const material = new THREE.MeshBasicMaterial({
      map: this.fogTexture,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    this.fogMesh = new THREE.Mesh(geometry, material);
    this.fogMesh.rotation.x = -Math.PI / 2; // Lay flat
    this.fogMesh.position.set(
      gridInfo.offsetX + worldWidth / 2,
      0.5, // Just above terrain surface
      gridInfo.offsetZ + worldHeight / 2
    );
    this.fogMesh.renderOrder = 999; // Render on top
    this.fogMesh.visible = false; // Disabled by default - press V to toggle

    this.scene.add(this.fogMesh);
  }

  update(deltaTime: number): void {
    if (!this.visionSystem || !this.playerFaction || !this.fogData || !this.fogTexture) return;

    this.updateTimer += deltaTime;
    if (this.updateTimer < this.updateInterval) return;
    this.updateTimer = 0;

    const visionGrid = this.visionSystem.getVisionGrid(this.playerFaction);
    if (!visionGrid) return;

    const gridInfo = this.visionSystem.getGridInfo();
    const width = gridInfo.width;
    const height = gridInfo.height;

    // Update fog texture based on vision
    for (let z = 0; z < height; z++) {
      for (let x = 0; x < width; x++) {
        const visionIdx = z * width + x;
        const texIdx = ((height - 1 - z) * width + x) * 4; // Flip Y for texture

        const vision = visionGrid[visionIdx];

        if (vision === 2) {
          // Visible - fully transparent
          this.fogData[texIdx] = 0;
          this.fogData[texIdx + 1] = 0;
          this.fogData[texIdx + 2] = 0;
          this.fogData[texIdx + 3] = 0;
        } else if (vision === 1) {
          // Explored - light overlay
          this.fogData[texIdx] = 30;
          this.fogData[texIdx + 1] = 30;
          this.fogData[texIdx + 2] = 40;
          this.fogData[texIdx + 3] = 60;
        } else {
          // Unexplored - medium overlay
          this.fogData[texIdx] = 20;
          this.fogData[texIdx + 1] = 20;
          this.fogData[texIdx + 2] = 30;
          this.fogData[texIdx + 3] = 120;
        }
      }
    }

    this.fogTexture.needsUpdate = true;
  }

  /**
   * Check if fog of war is enabled
   */
  isEnabled(): boolean {
    return this.fogMesh !== null && this.fogMesh.visible;
  }

  /**
   * Toggle fog of war visibility
   */
  setEnabled(enabled: boolean): void {
    if (this.fogMesh) {
      this.fogMesh.visible = enabled;
    }
  }

  /**
   * Toggle fog visibility
   */
  toggle(): void {
    if (this.fogMesh) {
      this.fogMesh.visible = !this.fogMesh.visible;
    }
  }

  dispose(): void {
    if (this.fogMesh) {
      this.scene.remove(this.fogMesh);
      this.fogMesh.geometry.dispose();
      (this.fogMesh.material as THREE.Material).dispose();
    }
    if (this.fogTexture) {
      this.fogTexture.dispose();
    }
    this.fogMesh = null;
    this.fogTexture = null;
    this.fogData = null;
  }
}
